/**
 * Generic, schema-driven zod validation and draft conversion — reusable by
 * any config page's `ConfigSchema.Shape`, not Kora-specific. A page's own
 * composition over its schema (e.g. `KoraConfig.Draft`'s `toDraft` /
 * `fromDraft`) lives beside that page, in `configs/<page>/<page>-serializers.ts`.
 */
import type { Duration, Extension } from "@medplum/fhirtypes";
import type { TFunction } from "i18next";
import { z } from "zod";

import type {
  ConfigNumberValidation,
  ConfigSchema,
} from "@/types/clinic-configuration";
import type { WeekSchedule } from "@/types/scheduling";
import { DAY_CODES } from "@/constants/scheduling.constants";
import {
  buildDurationQuantity,
  isWeekSchedule,
  parseDurationSeconds,
} from "@/lib/utils/helpers/clinic-configuration";
import {
  createEmptyWeekSchedule,
  hasRangeOverlap,
  isValidRange,
} from "@/lib/utils/helpers/scheduling";
import {
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isString,
} from "@/lib/utils/typeof";

/**
 * The kinds this generic layer can turn into sub-extensions on its own, and
 * the only step `weekSchedule` is excluded from — permanently. Its wire
 * shape is three sub-slugs the owning page declares, so only that page can
 * flatten it. Everything else here — drafting, validation, input rendering,
 * url ownership — takes the whole schema.
 */
export const isWritableField = (
  field: ConfigSchema.Field
): field is ConfigSchema.WritableField => field.kind !== "weekSchedule";

/**
 * The set the *user* can type into: everything but the fields some other
 * system assigns. A `readonly` field still travels through the draft and
 * back onto the wire unchanged — it is only validation and input rendering
 * that skip it.
 */
export const isEditableField = (field: ConfigSchema.Field): boolean =>
  !field.readonly;

/**
 * A schema's `extensions` holds both simple extensions (one field) and
 * complex ones (a section of sub-extensions). Only a section is a card.
 */
export const isSection = (
  extension: ConfigSchema.ExtensionField | ConfigSchema.Section
): extension is ConfigSchema.Section =>
  extension.kind === "object" || extension.kind === "array";

/**
 * A schema's sections in key order, which is the order they render in.
 * Keys stay typed, so a page can index its parsed model with them.
 */
export const getSchemaSections = <
  Extensions extends Record<
    string,
    ConfigSchema.ExtensionField | ConfigSchema.Section
  >,
>(
  extensions: Extensions
) =>
  (
    Object.entries(extensions) as [
      keyof Extensions & string,
      ConfigSchema.ExtensionField | ConfigSchema.Section,
    ][]
  ).filter(
    (entry): entry is [keyof Extensions & string, ConfigSchema.Section] =>
      isSection(entry[1])
  );

/**
 * The empty-draft value per kind — what `toSectionDraft` seeds a missing
 * value with, and what `fromSectionDraft` reads back as "not set."
 * Booleans, lists and week schedules have no such sentinel (see
 * `fromSectionDraft`): the value here is a seed only, and an unset optional
 * boolean round-trips as `false` rather than `undefined`.
 *
 * Factories rather than values, because the two non-scalar kinds are seeded
 * into every field that needs one: one shared array or week object would be
 * handed to every list and every schedule on the page, so a mutation through
 * any of them would reach all the others.
 *
 * `number` is `""`, not `0`. `0` cannot be the sentinel because it is also a
 * legitimate value — `autoRate: 0` means "never auto-escalate", and the
 * parser already treats a stored `0` as present rather than absent. With `0`
 * as the sentinel, saving zero made `fromSectionDraft` report the field as
 * unset, which the write path reads as "delete it from the resource."
 */
const CREATE_EMPTY_DRAFT_VALUE: Record<
  ConfigSchema.Field["kind"],
  () => string | number | boolean | string[] | WeekSchedule
> = {
  text: () => "",
  code: () => "",
  timezone: () => "",
  number: () => "",
  percent: () => "",
  boolean: () => false,
  duration: () => "",
  list: () => [],
  weekSchedule: createEmptyWeekSchedule,
};

/**
 * A week with no day switched on is a follow-up that never sends — the
 * composite's own "not set", since it has no empty sentinel a scalar would
 * use.
 */
const hasSendWindow = (schedule: WeekSchedule): boolean =>
  DAY_CODES.some(
    (day) => schedule[day].enabled && schedule[day].ranges.length > 0
  );

/**
 * `refine` rather than `int`: zod 4 models `int` as a number *format*, so a
 * fraction aborts its option and the surrounding
 * `z.union([z.literal(""), …])` falls back to the empty sentinel's own
 * wording — the field read `Invalid input: expected ""`. A refinement runs on
 * an already-typed number, which the union does surface.
 */
const wholeNumber = (t: TFunction): z.ZodCoercedNumber =>
  z.coerce.number().refine(Number.isInteger, t("Must be a whole number."));

/**
 * The whole-number check is read off `valueType` rather than a `validation`
 * rule of its own: the wire type is what decides whether a fraction is
 * storable at all, so deriving it here is what keeps this schema from
 * accepting a value `buildFieldExtension` would then write as a fractional
 * `valueInteger`.
 *
 * Only an extension declares a wire type today. A direct property field
 * has no way yet to say its FHIR type is `integer` or `positiveInt`, so
 * it gets no whole-number rule until one needs it.
 */
const isIntegerField = (field: ConfigSchema.NumberField): boolean =>
  "valueType" in field && field.valueType === "integer";

/**
 * One rule per primitive value — string, number, one-of — each with the
 * field's `required` already applied, so a kind only picks its primitive and
 * states its own bounds. `list` and `weekSchedule` are not primitives: each
 * has its own meaning of "empty", so they stay hand-written.
 */
const createFieldRules = (
  { required }: Pick<ConfigSchema.Field, "required">,
  t: TFunction
) => {
  /**
   * The one place `required` is applied. Each shape below says what "empty"
   * means for its value; this only rejects that value with the shared
   * message.
   */
  const requirable = <Value>(
    rule: z.ZodType<Value>,
    isEmpty: (value: Value) => boolean
  ): z.ZodType<Value> =>
    required === true
      ? rule.refine((value) => !isEmpty(value), t("This field is required."))
      : rule;

  /**
   * For every kind whose draft seeds `""` as "not set" (see
   * `CREATE_EMPTY_DRAFT_VALUE`): without the sentinel, the value's own rule
   * would reject that seed, and an optional field would open the form
   * already invalid and block the save.
   *
   * `z.literal("")` must come first: `z.coerce.number()` turns `""` into `0`
   * (`Number("") === 0`), so coercing first would silently read a cleared
   * input as zero. Matching the empty sentinel up front keeps "cleared" and
   * "zero" distinct all the way to the wire.
   */
  const withEmptySentinel = <Value>(rule: z.ZodType<Value>) =>
    requirable(z.union([z.literal(""), rule]), (value) => value === "");

  return {
    text: ({
      pattern,
      format,
    }: NonNullable<ConfigSchema.TextField["validation"]> = {}) => {
      const base = z.string().trim();

      /**
       * `refine` rather than `regex`, with the empty case skipped: an
       * optional patterned field can still be left blank, and an empty
       * required one reports only "required", not a format error too.
       */
      const patterned = pattern
        ? base.refine(
            (value) => value === "" || pattern.regex.test(value),
            t(pattern.message)
          )
        : base;

      /**
       * `httpUrl` parses the value, which a pattern cannot: `https://:80`
       * looks like a link and is not one. It still allows a space in the
       * path, which would cut the link short in a patient's message.
       */
      const formatted =
        format === "url"
          ? patterned.refine(
              (value) =>
                value === "" ||
                (!/\s/.test(value) && z.httpUrl().safeParse(value).success),
              t("Enter a full link starting with http:// or https://")
            )
          : patterned;

      return requirable(formatted, (value) => value === "");
    },
    number: ({
      integer,
      min,
      max,
    }: ConfigNumberValidation & { integer?: boolean }) => {
      const base = integer ? wholeNumber(t) : z.coerce.number();
      const bounded = isNumber(min)
        ? base.min(min, t("Must be {{min}} or more.", { min }))
        : base;

      return withEmptySentinel(
        isNumber(max)
          ? bounded.max(max, t("Must be {{max}} or less.", { max }))
          : bounded
      );
    },
    oneOf: (keys: [string, ...string[]]) => withEmptySentinel(z.enum(keys)),
  };
};

const buildFieldSchema = (
  key: string,
  field: ConfigSchema.Field,
  t: TFunction
): z.ZodType => {
  const rules = createFieldRules({ required: field.required }, t);

  switch (field.kind) {
    case "text":
      return rules.text(field.validation);
    case "number":
      return rules.number({
        ...field.validation,
        integer: isIntegerField(field),
      });
    /**
     * The duration control already emits rounded, non-negative seconds, and
     * the percent control clamps to 0–100 and converts to this decimal
     * itself — these bounds guard a hand-written schema or a future caller,
     * not the UI.
     */
    case "duration":
      return rules.number({ integer: true, min: 0 });
    case "percent":
      return rules.number({ min: 0, max: 1 });
    case "boolean":
      return z.boolean();
    case "list": {
      const schema = z.array(z.string().trim().min(1));
      return field.required === true
        ? schema.min(1, t("This field is required."))
        : schema;
    }
    /**
     * The plain text rule, not one-of the picker's zones: a stored alias or
     * typo must not block saving the record's other fields.
     */
    case "timezone":
      return rules.text();
    case "code": {
      const keys = Object.keys(field.labels);
      if (keys.length === 0) {
        throw new Error(`Config field "${key}" has an empty label map.`);
      }
      return rules.oneOf(keys as [string, ...string[]]);
    }
    case "weekSchedule": {
      /**
       * Hand-written rather than composed from the scalar rules above: the
       * value is a whole week, so the structural guard the read path already
       * uses is what stands in for a datatype rule. The range check is the
       * one thing the editor cannot make impossible — a time is typed, so an
       * end can land before its start.
       */
      const schema = z
        .custom<WeekSchedule>(isWeekSchedule, t("This field is required."))
        .refine(
          (schedule) =>
            DAY_CODES.every(
              (day) =>
                !schedule[day].enabled ||
                schedule[day].ranges.every(isValidRange)
            ),
          t("Each time range must end after it starts.")
        )
        .refine(
          (schedule) =>
            DAY_CODES.every(
              (day) =>
                !schedule[day].enabled || !hasRangeOverlap(schedule[day].ranges)
            ),
          t("Time ranges on the same day must not overlap.")
        );
      return field.required === true
        ? schema.refine(hasSendWindow, t("This field is required."))
        : schema;
    }
  }
};

/**
 * Factory, not a module-level const — matches `createInviteSchema(t)` in
 * `InviteMemberDialog.tsx`, so validation messages are translated rather
 * than frozen in English.
 *
 * Both section kinds go through this: an `array` section's fields describe
 * one entry, so this is the per-entry schema its form wraps in `z.array`.
 */
export const createSectionSchema = (
  section: { fields: ConfigSchema.Shape },
  t: TFunction
) => {
  const editableFields = Object.entries(section.fields).filter(([, field]) =>
    isEditableField(field)
  );
  const schema = z.object(
    Object.fromEntries(
      editableFields.map(([key, field]) => [
        key,
        buildFieldSchema(key, field, t),
      ])
    )
  );

  /**
   * The field's own `required` rule, built a second time and run only while
   * its switch is on — so "empty" means exactly what it means for a field
   * that is always required, kind by kind.
   */
  const conditionalRules = editableFields.flatMap(([key, field]) => {
    const { requiredWhen } = field;
    if (!requiredWhen) {
      return [];
    }

    /**
     * The control must be editable, not only present: `z.object` strips the
     * keys it has no schema for, so a readonly switch would never reach the
     * refinement below and the rule would silently never apply.
     */
    const control = section.fields[requiredWhen];
    if (control?.kind !== "boolean" || !isEditableField(control)) {
      throw new Error(
        `Config field "${key}" is requiredWhen "${requiredWhen}", which is not an editable boolean field of its section.`
      );
    }

    return [
      {
        key,
        controlKey: requiredWhen,
        requiredSchema: buildFieldSchema(key, { ...field, required: true }, t),
      },
    ];
  });

  if (conditionalRules.length === 0) {
    return schema;
  }

  return schema.superRefine((values, context) => {
    conditionalRules.forEach(({ key, controlKey, requiredSchema }) => {
      if (values[controlKey] !== true) {
        return;
      }

      const result = requiredSchema.safeParse(values[key]);
      if (!result.success) {
        context.addIssue({
          code: "custom",
          message: result.error.issues[0].message,
          path: [key],
        });
      }
    });
  });
};

/**
 * Save validates only the fields the user changed, but a `requiredWhen` field
 * is checked whenever its control is on — touched or not. A save rewrites the
 * whole block, and the agent rejects a block whose rule is broken, so an
 * unrelated edit saved over an already-broken block would be lost with it.
 *
 * Takes form paths, not bare keys, so in an array section each changed entry
 * (`entries.0.key`) is checked on its own.
 */
export const withRequiredWhenDependents = (
  fields: ConfigSchema.Shape,
  fieldNames: string[],
  getValue: (name: string) => unknown
): string[] => {
  const prefixes = new Set(
    fieldNames.map((name) => name.slice(0, name.lastIndexOf(".") + 1))
  );
  const dependentNames = [...prefixes].flatMap((prefix) =>
    Object.entries(fields)
      .filter(
        ([, field]) =>
          !!field.requiredWhen &&
          getValue(`${prefix}${field.requiredWhen}`) === true
      )
      .map(([key]) => `${prefix}${key}`)
  );

  return [...new Set([...fieldNames, ...dependentNames])];
};

/**
 * `model` is untyped at this boundary — the generic function can't thread a
 * config page's specific model-slice type through `Shape`. Callers cast the
 * result to their own `*Draft` type (itself `ConfigSchema.SectionDraft<S>`),
 * which is what keeps that one assertion from being repeated per field.
 */
export const toSectionDraft = <SectionShape extends ConfigSchema.Shape>(
  shape: SectionShape,
  model: Record<string, unknown>
): ConfigSchema.SectionDraft<SectionShape> => {
  const entries = Object.entries(shape).map(([key, field]) => [
    key,
    model[key] ?? CREATE_EMPTY_DRAFT_VALUE[field.kind](),
  ]);

  return Object.fromEntries(entries) as ConfigSchema.SectionDraft<SectionShape>;
};

/**
 * `toSectionDraft` per entry of an `array` section, stamping each with the
 * position it was parsed from. The model's entry order is the wire's — the
 * parser keeps stored order — so the array index is the stored block's
 * index, which is exactly what `ConfigSchema.ArrayEntryDraft.sourceIndex`
 * means.
 */
export const toSectionDraftEntries = <SectionShape extends ConfigSchema.Shape>(
  shape: SectionShape,
  models: Record<string, unknown>[]
): ConfigSchema.ArrayEntryDraft<SectionShape>[] =>
  models.map((model, index) => ({
    ...toSectionDraft(shape, model),
    sourceIndex: index,
  }));

/**
 * The kinds whose empty state is a value rather than a sentinel to map back:
 * a boolean's `false` is a value; a list's `[]` already writes nothing,
 * because `buildFieldExtension` emits one sub-extension per entry and an
 * empty list has none; and a week with every day off flattens to no
 * `send-window` at all, for the same reason.
 *
 * These are also exactly the kinds `CREATE_EMPTY_DRAFT_VALUE` builds fresh
 * each call, so skipping them is what keeps the `===` below comparing
 * primitives rather than two never-equal object references.
 */
const KINDS_WITHOUT_EMPTY_SENTINEL: ConfigSchema.Field["kind"][] = [
  "boolean",
  "list",
  "weekSchedule",
];

/**
 * The reverse of `toSectionDraft`: a value equal to the field's empty
 * sentinel reads back as `undefined` (not set), except the kinds above,
 * which have no sentinel and pass through unchanged. A required field never
 * round-trips through the empty branch in practice — valid submitted data
 * isn't empty.
 */
export const fromSectionDraft = <SectionShape extends ConfigSchema.Shape>(
  shape: SectionShape,
  draft: ConfigSchema.SectionDraft<SectionShape>
): Record<string, unknown> => {
  const draftValues = draft as Record<string, unknown>;

  const entries = Object.entries(shape).map(([key, field]) => {
    const draftValue = draftValues[key];
    /**
     * The draft is the raw form state, not the validated output, so this is
     * where `createSectionSchema`'s `trim` reaches the wire: padding is not
     * stored, and a blank value is left out like an empty one rather than
     * written. A readonly value is some other system's, so it is kept as is.
     */
    const value =
      field.kind === "text" && isEditableField(field) && isString(draftValue)
        ? draftValue.trim()
        : draftValue;
    if (KINDS_WITHOUT_EMPTY_SENTINEL.includes(field.kind)) {
      return [key, value];
    }
    return [
      key,
      value === CREATE_EMPTY_DRAFT_VALUE[field.kind]() ? undefined : value,
    ];
  });

  return Object.fromEntries(entries);
};

/**
 * A stored value as the field's model value, kept only if its JSON type
 * fits the field's `kind` (for a `list`, only the entries that fit). Anything else reads as absent, the same as an
 * extension that does not parse: a wrong-typed value must not reach a
 * control that cannot show it, nor be written back as if it were valid.
 */
export const readFieldValue = (
  field: ConfigSchema.Field,
  stored: unknown
): unknown => {
  switch (field.kind) {
    case "text":
    case "code":
    case "timezone":
      return isString(stored) ? stored : undefined;
    /**
     * `isNumber` also accepts a numeric string, which is not a stored
     * number.
     */
    case "number":
    case "percent":
      return isNumber(stored) && !isString(stored) ? stored : undefined;
    case "boolean":
      return isBoolean(stored) ? stored : undefined;
    case "duration":
      return isObject<Duration>(stored)
        ? parseDurationSeconds(stored)
        : undefined;
    /**
     * Keeps the readable entries, as the Kora parser does for its lists: one
     * bad entry must not empty the whole list, or the next save of it
     * would delete every stored value.
     */
    case "list":
      return isArray(stored) ? stored.filter(isString) : undefined;
    case "weekSchedule":
      return isWeekSchedule(stored) ? stored : undefined;
  }
};

/**
 * `wrap` exists for `valueDuration` alone: the draft holds a plain number of
 * seconds and FHIR requires a quantity there, so a bare
 * `valueDuration: 172800` is invalid. Key and wrapper share one table so that
 * a new wire type cannot be declared in one and forgotten in the other.
 */
const FHIR_WIRE: Record<
  NonNullable<ConfigSchema.ExtensionWire["valueType"]>,
  { key: string; wrap?: (value: unknown) => unknown }
> = {
  string: { key: "valueString" },
  decimal: { key: "valueDecimal" },
  integer: { key: "valueInteger" },
  boolean: { key: "valueBoolean" },
  code: { key: "valueCode" },
  duration: {
    key: "valueDuration",
    /**
     * A non-number here means the draft and the schema disagree — a caller
     * bug, not a value to quietly drop.
     */
    wrap: (value) => {
      if (!isNumber(value)) {
        throw new Error(
          `Duration field received a non-numeric value: ${String(value)}.`
        );
      }
      return buildDurationQuantity(Number(value));
    },
  },
};

/**
 * A kind left out here must declare `valueType` on the schema: `number` and
 * `code` are genuinely ambiguous. Enforced at runtime, not by the type
 * system, because `valueType` is optional on `ExtensionWire` for every kind.
 * `percent` is unambiguous — it is always a FHIR decimal — so it defaults
 * here the same way `duration` does.
 */
const DEFAULT_VALUE_TYPE: Partial<
  Record<
    ConfigSchema.WritableExtensionField["kind"],
    NonNullable<ConfigSchema.ExtensionWire["valueType"]>
  >
> = {
  text: "string",
  /**
   * The HL7 `timezone` extension is a `valueCode`.
   */
  timezone: "code",
  boolean: "boolean",
  duration: "duration",
  list: "string",
  percent: "decimal",
};

const resolveValueType = (
  field: ConfigSchema.WritableExtensionField
): NonNullable<ConfigSchema.ExtensionWire["valueType"]> => {
  if (field.valueType) {
    return field.valueType;
  }

  const fallback = DEFAULT_VALUE_TYPE[field.kind];
  if (!fallback) {
    throw new Error(
      `Config field "${field.slug}" (kind "${field.kind}") requires an explicit valueType.`
    );
  }
  return fallback;
};

/**
 * The `value[x]` key a simple extension field is stored under, e.g.
 * `valueString` — what a reader looks the value up by.
 */
export const getExtensionValueKey = (
  field: ConfigSchema.WritableExtensionField
): string => FHIR_WIRE[resolveValueType(field)].key;

/**
 * `Extension[]` for every kind, not just `list` — one shape keeps
 * `buildSectionExtensions`'s `flatMap` free of a per-kind branch, which is
 * the whole reason a repeating sub-extension goes through this function
 * rather than a parallel one.
 */
export const buildFieldExtension = (
  field: ConfigSchema.WritableExtensionField,
  value: unknown
): Extension[] => {
  const { key, wrap } = FHIR_WIRE[resolveValueType(field)];
  const build = (entry: unknown): Extension => ({
    url: field.slug,
    [key]: wrap ? wrap(entry) : entry,
  });

  if (field.kind !== "list") {
    return [build(value)];
  }

  /**
   * A non-array here means the draft and the schema disagree — same call-site
   * bug the duration wrapper throws on, not a value to quietly drop.
   */
  if (!isArray(value)) {
    throw new Error(
      `List field "${field.slug}" received a non-array value: ${String(value)}.`
    );
  }
  return value.map(build);
};

/**
 * One sub-extension per writable field with a defined value — or one per
 * entry, for a `list`. A field whose value is `undefined` (an unset
 * optional, per `fromSectionDraft`) is omitted rather than written as an
 * empty sub-extension. A `weekSchedule` is left out entirely: the page that
 * declares its sub-slugs appends those blocks to this output.
 */
export const buildSectionExtensions = (
  shape: ConfigSchema.ExtensionShape,
  values: Record<string, unknown>
): Extension[] => {
  const entries = Object.entries(shape).filter(
    (entry): entry is [string, ConfigSchema.WritableExtensionField] =>
      isWritableField(entry[1])
  );

  return entries
    .filter(([key]) => values[key] !== undefined)
    .flatMap(([key, field]) => buildFieldExtension(field, values[key]));
};

/**
 * Every sub-extension URL a section's schema declares — what a patch
 * builder's merge-don't-replace step preserves all the others against.
 * `weekSchedule` is included even though `buildSectionExtensions` skips it:
 * the owning page re-emits those sub-extensions itself, so leaving them out
 * here would preserve the stored windows as foreign and write the edited
 * ones beside them.
 */
export const getSectionOwnedUrls = (
  shape: ConfigSchema.ExtensionShape
): Set<string> => new Set(Object.values(shape).map((field) => field.slug));
